import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  deleteProduct,
  getCategories,
  getProductById,
  updateProduct,
} from "../utils/api";

export default function EditItem({ token, currentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const [item, setItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [name, setName] = useState("");
  const [productCategoryId, setProductCategoryId] = useState("");
  const [price, setPrice] = useState("");
  const [itemCondition, setItemCondition] = useState("New");
  const [status, setStatus] = useState("available");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setMsg("");

        const [it, cats] = await Promise.all([
          getProductById(id),
          getCategories(),
        ]);
        if (cancelled) return;

        setItem(it || null);
        setCategories(Array.isArray(cats) ? cats : []);

        setName(it?.name || "");
        setProductCategoryId(
          String(it?.product_category_id || (cats?.[0]?.id ?? "")),
        );
        setPrice(String(it?.price ?? ""));
        setItemCondition(it?.item_condition || it?.condition || "New");
        setStatus(it?.status || "available");
        setDescription(it?.description || "");
      } catch (e) {
        if (!cancelled) setMsg(e?.message || "Failed to load item");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function onSave(e) {
    e.preventDefault();
    setMsg("");

    if (!token) return setMsg("Login first.");

    try {
      setSaving(true);
      const fd = new FormData();
      fd.append("name", name);
      fd.append("product_category_id", productCategoryId);
      fd.append("price", String(price));
      fd.append("item_condition", itemCondition);
      fd.append("status", status);
      fd.append("description", description);
      if (imageFile) fd.append("image", imageFile);

      await updateProduct(token, id, fd);
      navigate("/my-listings");
    } catch (err) {
      setMsg(err?.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    setMsg("");
    if (!token) return setMsg("Login first.");

    const ok = window.confirm("Delete this listing?");
    if (!ok) return;

    try {
      await deleteProduct(token, id);
      navigate("/my-listings");
    } catch (err) {
      setMsg(err?.message || "Failed to delete");
    }
  }

  if (!currentUser) {
    return (
      <div className="container pagePadBottom" style={{ maxWidth: 720 }}>
        <div className="card">
          <div style={{ fontWeight: 900, fontSize: 20 }}>Edit Item</div>
          <div className="muted" style={{ marginTop: 6 }}>
            Login first.
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <Link className="btnPrimary" to="/login">
              Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const role = String(currentUser?.role || "").toLowerCase();
  if (role !== "seller" && role !== "admin") {
    return (
      <div className="container pagePadBottom" style={{ maxWidth: 720 }}>
        <div className="card">
          <div style={{ fontWeight: 900, fontSize: 20 }}>Edit Item</div>
          <div className="muted" style={{ marginTop: 6 }}>
            Only sellers can edit listings.
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container pagePadBottom" style={{ maxWidth: 820 }}>
        <div className="card">Loading…</div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container pagePadBottom" style={{ maxWidth: 820 }}>
        <div className="card">
          <div style={{ fontWeight: 900, fontSize: 20 }}>Item not found</div>
          <div className="row" style={{ marginTop: 12 }}>
            <Link className="btnSecondary" to="/my-listings">
              Back
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const sellerId = String(item?.seller_user_id ?? item?.user_id ?? "");
  const canEdit =
    currentUser.role === "admin" || String(currentUser.id) === sellerId;

  if (!canEdit) {
    return (
      <div className="container pagePadBottom" style={{ maxWidth: 820 }}>
        <div className="card">
          <div style={{ fontWeight: 900, fontSize: 20 }}>Forbidden</div>
          <div className="muted" style={{ marginTop: 6 }}>
            You can only edit your own listings.
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <Link className="btnSecondary" to="/my-listings">
              Back
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container pagePadBottom" style={{ maxWidth: 820 }}>
      <div
        className="row"
        style={{
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontWeight: 900, fontSize: 22 }}>Edit listing</div>
          <div className="muted" style={{ marginTop: 6, fontSize: 13 }}>
            Update details, or delete the listing.
          </div>
        </div>
        <Link className="btnSecondary" to="/my-listings">
          Back
        </Link>
      </div>

      <div className="card" style={{ marginTop: 12 }}>
        <form onSubmit={onSave} style={{ display: "grid", gap: 12 }}>
          <div>
            <div className="label">Item name</div>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div>
            <div className="label">Category</div>
            <select
              className="input"
              value={productCategoryId}
              onChange={(e) => setProductCategoryId(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.title ?? c.label ?? "Category"}
                </option>
              ))}
            </select>
          </div>

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="label">Price ($)</div>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </div>

            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="label">Condition</div>
              <select
                className="input"
                value={itemCondition}
                onChange={(e) => setItemCondition(e.target.value)}
              >
                <option>New</option>
                <option>Like New</option>
                <option>Used</option>
              </select>
            </div>
          </div>

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="label">Status</div>
              <select
                className="input"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="available">available</option>
                <option value="reserved">reserved</option>
                <option value="sold">sold</option>
                <option value="removed">removed</option>
              </select>
            </div>

            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="label">Replace image (optional)</div>
              <input
                className="input"
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>

          <div>
            <div className="label">Description</div>
            <textarea
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          {msg ? (
            <div className="card" style={{ borderLeft: "4px solid #e11d48" }}>
              <div style={{ fontWeight: 900 }}>{msg}</div>
            </div>
          ) : null}

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <button className="btnPrimary" type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>

            <button className="btnDanger" type="button" onClick={onDelete}>
              Delete
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
